import { CheckCircleIcon, EnvelopeSimpleIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { BotonAuth, CampoAuth } from '@/components/CampoAuth'
import { Logo } from '@/components/Logo'
import { ApiError, type FieldErrors, auth } from '@/lib/api'
import { haptic } from '@/lib/haptics'

/**
 * Recuperar el acceso.
 *
 * Dos pantallas y las dos van **antes** del portal de sesión: quien olvidó su
 * contraseña por definición no puede entrar, así que ponerlas detrás del acceso
 * las haría inalcanzables.
 *
 * Hasta ahora no existían. Olvidar la contraseña era perder la cuenta con toda
 * su historia dentro, y la única salida era que alguien la cambiara a mano en la
 * base de datos. Eso ya pasó una vez.
 */

// ═══════════════════════════════════════════════════════════════════════════
//  Pedir el enlace
// ═══════════════════════════════════════════════════════════════════════════

export function PedirEnlace() {
  const navigate = useNavigate()
  const [enviando, setEnviando] = useState(false)
  const [listo, setListo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [campos, setCampos] = useState<FieldErrors>({})

  const enviar = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    setEnviando(true)
    setError(null)
    setCampos({})

    const correo = String(new FormData(event.currentTarget).get('email'))
    try {
      await auth.forgotPassword(correo)
      haptic('success')
      setListo(correo)
    } catch (cause) {
      haptic('error')
      if (cause instanceof ApiError) {
        setError(cause.message)
        setCampos(cause.fieldErrors ?? {})
      } else {
        setError('No pudimos mandar el correo. Revisa tu conexión e inténtalo otra vez.')
      }
      setEnviando(false)
    }
  }

  // La confirmación no dice «te mandamos un correo» sino «si ese correo tiene
  // cuenta»: es lo único cierto que se puede decir sin revelar quién está
  // registrado. Se repite la dirección para que un dedazo se note aquí y no
  // después de diez minutos esperando frente a la bandeja.
  if (listo !== null) {
    return (
      <Marco titulo="Revisa tu correo">
        <div className="flex flex-col items-center gap-3 rounded-card bg-income-soft px-5 py-8 text-center">
          <CheckCircleIcon size={30} weight="fill" className="text-income-text" />
          <p className="text-[15px] leading-snug text-income-text">
            Si <span className="font-semibold">{listo}</span> tiene una cuenta en PayBae, ya salió
            el enlace para cambiar la contraseña.
          </p>
        </div>
        <p className="mt-4 px-1 text-[13px] leading-relaxed text-ink-soft">
          El enlace vence en una hora. Si no lo ves, busca en correo no deseado antes de pedir
          otro.
        </p>
        <div className="mt-6 flex flex-col gap-3">
          <BotonAuth type="button" onClick={() => navigate('/', { replace: true })}>
            Volver al acceso
          </BotonAuth>
          <BotonAuth
            type="button"
            variante="contorno"
            onClick={() => {
              setListo(null)
              setEnviando(false)
            }}
          >
            Usar otro correo
          </BotonAuth>
        </div>
      </Marco>
    )
  }

  return (
    <Marco titulo="Recupera tu acceso" icono={<EnvelopeSimpleIcon size={26} weight="fill" />}>
      <p className="mb-6 px-1 text-[15px] leading-snug text-ink-soft">
        Escribe el correo de tu cuenta y te mandamos un enlace para elegir una contraseña nueva.
      </p>

      <form onSubmit={enviar} className="flex flex-col gap-3" noValidate>
        <CampoAuth
          label="Tu correo"
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          errores={campos.email}
        />
        {error ? (
          <p
            role="alert"
            className="rounded-control bg-expense-soft px-4 py-3 text-[13px] text-expense-text"
          >
            {error}
          </p>
        ) : null}
        <div className="mt-3">
          <BotonAuth enviando={enviando}>Mandar el enlace</BotonAuth>
        </div>
      </form>

      <p className="py-8 text-center text-[14px] text-ink-soft">
        ¿Ya te acordaste?{' '}
        <button
          type="button"
          onClick={() => navigate('/', { replace: true })}
          className="min-h-[44px] font-semibold text-primary-text active:opacity-70"
        >
          Entra
        </button>
      </p>
    </Marco>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
//  Elegir la contraseña nueva
// ═══════════════════════════════════════════════════════════════════════════

export function NuevaContrasena() {
  const { token = '' } = useParams()
  const navigate = useNavigate()
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [campos, setCampos] = useState<FieldErrors>({})
  const [listo, setListo] = useState(false)

  // Se comprueba antes de dibujar el formulario. Pedir una contraseña nueva y
  // rechazarla al enviar —cuando el problema era el enlace, no la contraseña—
  // es hacer perder el tiempo a quien ya venía con prisa.
  const enlace = useQuery({
    queryKey: ['recuperar', token],
    queryFn: () => auth.checkResetToken(token),
    retry: false,
  })

  const enviar = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    const datos = new FormData(event.currentTarget)
    const clave = String(datos.get('password'))

    if (clave !== String(datos.get('passwordConfirm'))) {
      setCampos({ passwordConfirm: ['Las dos contraseñas no coinciden.'] })
      haptic('error')
      return
    }

    setEnviando(true)
    setError(null)
    setCampos({})
    try {
      await auth.resetPassword(token, clave)
      haptic('success')
      setListo(true)
    } catch (cause) {
      haptic('error')
      if (cause instanceof ApiError) {
        setError(cause.message)
        setCampos(cause.fieldErrors ?? {})
      } else {
        setError('No pudimos cambiarla. Inténtalo otra vez.')
      }
      setEnviando(false)
    }
  }

  if (enlace.isPending) {
    return (
      <Marco titulo="Un momento">
        <div className="h-32 animate-pulse rounded-card bg-surface-sunken" />
      </Marco>
    )
  }

  if (enlace.isError || !enlace.data?.valido) {
    return (
      <Marco titulo="Este enlace ya no sirve">
        <div className="flex flex-col items-center gap-3 rounded-card bg-expense-soft px-5 py-8 text-center">
          <WarningCircleIcon size={30} weight="fill" className="text-expense-text" />
          <p className="text-[15px] leading-snug text-expense-text">
            Los enlaces vencen a la hora y solo se pueden usar una vez. Pide uno nuevo y ábrelo
            en cuanto llegue.
          </p>
        </div>
        <div className="mt-6">
          <BotonAuth type="button" onClick={() => navigate('/recuperar', { replace: true })}>
            Pedir otro enlace
          </BotonAuth>
        </div>
      </Marco>
    )
  }

  if (listo) {
    return (
      <Marco titulo="Contraseña cambiada">
        <div className="flex flex-col items-center gap-3 rounded-card bg-income-soft px-5 py-8 text-center">
          <CheckCircleIcon size={30} weight="fill" className="text-income-text" />
          <p className="text-[15px] leading-snug text-income-text">
            Ya puedes entrar con tu contraseña nueva.
          </p>
        </div>
        {/* Se dice antes de que lo descubran solos en el otro teléfono. */}
        <p className="mt-4 px-1 text-[13px] leading-relaxed text-ink-soft">
          Por seguridad se cerró la sesión en todos tus dispositivos, así que vas a tener que
          volver a entrar en cada uno.
        </p>
        <div className="mt-6">
          <BotonAuth type="button" onClick={() => navigate('/', { replace: true })}>
            Entrar a PayBae
          </BotonAuth>
        </div>
      </Marco>
    )
  }

  return (
    <Marco titulo="Elige una contraseña nueva">
      <p className="mb-6 px-1 text-[15px] leading-snug text-ink-soft">
        Hola{enlace.data.nombre ? `, ${enlace.data.nombre.split(' ')[0]}` : ''}. Escribe la que vas
        a usar de ahora en adelante.
      </p>

      <form onSubmit={enviar} className="flex flex-col gap-3" noValidate>
        {/* El correo, oculto pero presente: sin él los gestores de contraseñas
            no saben a qué cuenta pertenece la nueva y no la guardan. */}
        <input type="hidden" name="username" autoComplete="username" value={enlace.data.nombre ?? ''} readOnly />
        <CampoAuth
          label="Contraseña nueva"
          name="password"
          type="password"
          autoComplete="new-password"
          ayuda="Mínimo 10 caracteres."
          errores={campos.password}
        />
        <CampoAuth
          label="Repítela"
          name="passwordConfirm"
          type="password"
          autoComplete="new-password"
          errores={campos.passwordConfirm}
        />
        {error ? (
          <p
            role="alert"
            className="rounded-control bg-expense-soft px-4 py-3 text-[13px] text-expense-text"
          >
            {error}
          </p>
        ) : null}
        <div className="mt-3">
          <BotonAuth enviando={enviando}>Guardar y entrar</BotonAuth>
        </div>
      </form>
    </Marco>
  )
}

/** El mismo marco de la invitación: marca arriba, contenido centrado. */
function Marco({
  titulo,
  icono,
  children,
}: {
  titulo: string
  icono?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="min-h-dvh bg-bg">
      <div className="px-safe mx-auto w-full max-w-sm px-6 pt-safe">
        <header className="flex justify-center py-8">
          <Logo className="size-11" />
        </header>
        {icono ? (
          <span className="mb-4 flex size-14 items-center justify-center rounded-full bg-primary-soft text-primary-text">
            {icono}
          </span>
        ) : null}
        <h1 className="mb-5 font-display text-[28px] font-extrabold leading-[1.1] tracking-[-0.04em] text-ink">
          {titulo}
        </h1>
        {children}
      </div>
    </div>
  )
}
